/**
 * Candidate photo preview + upload/replace/edit/remove.
 * Used on Candidate Detail header and Resume Enrichment profile card.
 */
import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Pencil, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Avatar } from '@/components/Avatar';
import { PhotoCropDialog } from '@/components/PhotoCropDialog';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { v1Delete, v1Get, v1GetBlob, v1Upload } from '@/lib/api';
import { useHasPermission } from '@/auth/permissions';

const ALLOWED = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;

export function CandidatePhotoEditor({
  candidateId,
  candidateName,
  photoUrl: photoUrlProp,
  size = 56,
  evaluationId,
  className,
}) {
  const qc = useQueryClient();
  const canEdit = useHasPermission('candidate.edit');
  const fileRef = useRef(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const hasEval = Boolean(evaluationId);

  const photoQ = useQuery({
    queryKey: ['v1-candidate-photo', candidateId, evaluationId],
    queryFn: () => {
      const qs = `?evaluation_id=${encodeURIComponent(evaluationId)}`;
      return v1Get(`/candidates/${candidateId}/photo${qs}`);
    },
    enabled: Boolean(candidateId && evaluationId),
    staleTime: 30_000,
  });

  const photoUrl = photoQ.data?.photo_url ?? photoUrlProp ?? null;
  const hasCv = !!photoQ.data?.has_cv_photo;
  const hasManual = !!photoQ.data?.has_manual_photo;
  const hasAny = Boolean(photoUrl);

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: ['v1-candidate-photo', candidateId] });
    await qc.invalidateQueries({ queryKey: ['v1-candidate', candidateId] });
    await qc.invalidateQueries({ queryKey: ['v1-candidates'] });
    await qc.invalidateQueries({ queryKey: ['v1-resume-enrichment-queue'] });
  };

  const uploadMut = useMutation({
    mutationFn: (file) => {
      if (!evaluationId) throw new Error('evaluation_id is required');
      const qs = `?evaluation_id=${encodeURIComponent(evaluationId)}`;
      return v1Upload(`/candidates/${candidateId}/photo${qs}`, file);
    },
    onSuccess: async () => {
      toast.success('Photo saved');
      await invalidate();
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail || err?.message || 'Upload failed';
      toast.error(typeof detail === 'string' ? detail : 'Upload failed');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (fallback) => {
      if (!evaluationId) throw new Error('evaluation_id is required');
      const params = new URLSearchParams({ fallback, evaluation_id: evaluationId });
      return v1Delete(`/candidates/${candidateId}/photo?${params.toString()}`);
    },
    onSuccess: async () => {
      toast.success('Photo updated');
      setRemoveOpen(false);
      await invalidate();
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail || err?.message || 'Could not remove photo';
      toast.error(typeof detail === 'string' ? detail : 'Could not remove photo');
    },
  });

  const openFilePicker = () => {
    if (!canEdit) {
      toast.warning('You are not authorized to edit this photo.');
      return;
    }
    fileRef.current?.click();
  };

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED.includes((file.type || '').toLowerCase())) {
      toast.error('Use PNG, JPEG, or WebP.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Image must be 5 MB or smaller.');
      return;
    }
    const url = URL.createObjectURL(file);
    setCropSrc(url);
    setCropOpen(true);
  };

  const onEditExisting = async () => {
    if (!photoUrl) {
      openFilePicker();
      return;
    }
    // Load bytes through the API, not the presigned S3 URL: a cross-origin
    // image taints the crop canvas and export fails.
    setEditLoading(true);
    try {
      const qs = evaluationId ? `?evaluation_id=${encodeURIComponent(evaluationId)}` : '';
      const blob = await v1GetBlob(`/candidates/${candidateId}/photo/content${qs}`);
      setCropSrc(URL.createObjectURL(blob));
      setCropOpen(true);
    } catch {
      toast.error('Could not open the current photo. Try replacing it instead.');
    } finally {
      setEditLoading(false);
    }
  };

  if (!hasEval) {
    return (
      <div className={className} data-testid="candidate-photo-editor">
        <div className="flex items-center gap-3">
          <Avatar name={candidateName} size={size} src={photoUrlProp || undefined} />
          <p className="text-xs text-muted-foreground">Select an evaluation to manage the photo.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={className} data-testid="candidate-photo-editor">
      <div className="flex items-center gap-3">
        <Avatar name={candidateName} size={size} src={photoUrl || undefined} />
        {canEdit && (
          <div className="flex flex-wrap gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openFilePicker}
              disabled={uploadMut.isPending}
              data-testid="candidate-photo-upload"
            >
              {hasAny ? <Camera className="mr-1.5 h-3.5 w-3.5" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
              {hasAny ? 'Replace' : 'Upload'}
            </Button>
            {hasAny && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onEditExisting}
                disabled={uploadMut.isPending || editLoading}
                data-testid="candidate-photo-edit"
              >
                <Pencil className="mr-1.5 h-3.5 w-3.5" /> {editLoading ? 'Opening…' : 'Edit'}
              </Button>
            )}
            {hasAny && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setRemoveOpen(true)}
                disabled={deleteMut.isPending}
                data-testid="candidate-photo-remove"
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Remove
              </Button>
            )}
          </div>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={onFilePicked}
        data-testid="candidate-photo-file-input"
      />

      <PhotoCropDialog
        open={cropOpen}
        onOpenChange={(v) => {
          setCropOpen(v);
          if (!v && cropSrc?.startsWith('blob:')) URL.revokeObjectURL(cropSrc);
        }}
        imageSrc={cropSrc}
        onConfirm={async (file) => {
          await uploadMut.mutateAsync(file);
        }}
      />

      <AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <AlertDialogContent data-testid="candidate-photo-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove photo</AlertDialogTitle>
            <AlertDialogDescription>
              Choose what to show after removing the current photo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col gap-2 py-2">
            <Button
              type="button"
              variant="outline"
              disabled={!hasCv || deleteMut.isPending}
              onClick={() => deleteMut.mutate('resume')}
              data-testid="candidate-photo-fallback-resume"
            >
              Use resume photo
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={deleteMut.isPending}
              onClick={() => deleteMut.mutate('initials')}
              data-testid="candidate-photo-fallback-initials"
            >
              Use initials
            </Button>
            {!hasCv && hasManual && (
              <p className="text-xs text-muted-foreground">
                No resume photo is available — only initials can be used.
              </p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMut.isPending}>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
